import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BsImages } from "react-icons/bs";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import useWindowWidth from "../../utils/windowWidth";
import Chat from "../../components/Chat";
import ChatComposer from "../../components/ChatComposer";
import BouncingDotsLoader from "../../components/LoadingDots";
import axios from "axios";
import { FaCheckCircle, FaFileUpload, FaRedoAlt } from "react-icons/fa";


function BrowserAgent() {
    const [messages, setMessages] = useState([
        { id: Date.now(), text: "Hello! I'm your personal AI assistant.\n\nI can help you find and apply for jobs on Naukri, book rides with Ola, reserve travel tickets through Redbus, or order food from Swiggy — all in just a few seconds.\n\nJust tell me what you need, and I'll take care of it for you — quickly, easily, and while you're on the move.", sender: "other" },
    ]);
    const [newMessage, setNewMessage] = useState("");
    const [showImages, setShowImages] = useState(true);
    const socket = useRef(null);
    const hasConnected = useRef(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState("Connecting…");
    const [chatState, setChatState] = useState("initial");
    const wsUrl = process.env.REACT_APP_WS_URL || "wss://api-task-automation.istemai.com";
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [isButtonDisplayed, setIsButtonDisplayed] = useState(false);
    const cookieCheckIntervalRef = useRef(null);
    const mainRef = useRef(null);
    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 769;
    const liveRegionRef = useRef(null); //for screen readers
    const [showResumePanel, setShowResumePanel] = useState(false);
    const [resumeInfo, setResumeInfo] = useState({
        hasResume: false,
        resumeFileName: "",
    });
    const [resumeMode, setResumeMode] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [resumeMessage, setResumeMessage] = useState("");
    const [resumeError, setResumeError] = useState("");
    const resumeInputRef = useRef(null);


    const fetchResumeInfo = async () => {
        const userId = localStorage.getItem("userId");
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!userId || !apiUrl) return;

        try {
            const response = await axios.get(`${apiUrl}/profile/${userId}`);
            setResumeInfo({
                hasResume: response.data.has_resume || false,
                resumeFileName: response.data.resume_file_name || "",
            });
            setResumeMode(response.data.has_resume ? null : "replace");
        } catch (error) {
            console.error("Failed to fetch resume info:", error);
        }
    };

    useEffect(() => {
        if (
            localStorage.getItem("token") === null ||
            localStorage.getItem("token") === undefined
        ) {
            navigate("/login");
        }
        document.title = "Browser Agent - Nclude Portal";
    }, [navigate]);

    useEffect(() => {
        // Set focus on the main content when the route changes
        if (mainRef.current) {
            mainRef.current.focus();
        }
    }, [location]);

    useEffect(() => {
        if (hasConnected.current) return;
        hasConnected.current = true;

        const userId = localStorage.getItem("userId");
        socket.current = new WebSocket(`${wsUrl}/ws/chat/${userId}`);

        socket.current.onopen = () => {
            console.log("Connected to chat!");
            setIsConnected(true);
            setConnectionStatus("Online");
            setChatState("initial");
        };

        socket.current.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === "status") {
                switch (data.status) {
                    case "processing":
                        setChatState("processing");
                        break;
                    case "ready":
                        setChatState("end_turn");
                        break;
                    default:
                        break;
                }
            } else if (data.type === "message") {
                setMessages((prev) => [
                    ...prev,
                    { id: Date.now(), text: data.content, sender: "other" },
                ]);
            } else if (data.type === "image") {
                setMessages((prev) => [
                    ...prev,
                    {
                        id: Date.now(),
                        text: data.content,
                        sender: "other",
                        isImage: true,
                    },
                ]);
            } else if (data.type === "cookie-action") {
                if (data.content === "get-linkedin-cookies") {
                    handleCookieExtraction({
                        domainName: "LinkedIn",
                        domains: ["www.linkedin.com", ".linkedin.com"],
                        cookieName: "li_at",
                        successMessage:
                            "Successfully logged into your LinkedIn account.",
                        buttonText: "Open LinkedIn",
                        buttonLink: "https://www.linkedin.com",
                        socketMessageType: "linkedin-cookies",
                    });
                } else if (data.content === "get-naukri-cookies") {
                    handleCookieExtraction({
                        domainName: "Naukri",
                        domains: ["www.naukri.com", ".naukri.com"],
                        cookieName: "nauk_at",
                        successMessage:
                            "Successfully logged into your Naukri account.",
                        buttonText: "Open Naukri",
                        buttonLink: "https://www.naukri.com",
                        socketMessageType: "naukri-cookies",
                    });
                } else if (data.content === "get-swiggy-cookies") {
                    handleCookieExtraction({
                        domainName: "Swiggy",
                        domains: ["www.swiggy.com", ".swiggy.com"],
                        cookieName: "_is_logged_in",
                        successMessage:
                            "Successfully logged into your Swiggy account.",
                        buttonText: "Open Swiggy",
                        buttonLink: "https://www.swiggy.com",
                        socketMessageType: "swiggy-cookies",
                    });
                } else if (data.content === "get-uber-cookies") {
                    handleCookieExtraction({
                        domainName: "Uber",
                        domains: ["m.uber.com", ".uber.com", "www.uber.com"],
                        cookieName: "_ua",
                        successMessage:
                            "Successfully logged into your Uber account.",
                        buttonText: "Open Uber",
                        buttonLink: "https://www.uber.com",
                        socketMessageType: "uber-cookies",
                    });
                } else if (data.content === "get-1mg-cookies") {
                    handleCookieExtraction({
                        domainName: "1mg",
                        domains: [".1mg.com", "www.1mg.com"],
                        cookieName: "VISITOR-ID",
                        successMessage:
                            "Successfully logged into your 1mg account.",
                        buttonText: "Open 1mg",
                        buttonLink: "https://www.1mg.com",
                        socketMessageType: "1mg-cookies",
                    });
                }
            }
        };

        socket.current.onclose = (event) => {
            console.log("Disconnected from chat", event);
            setIsConnected(false);
            setConnectionStatus("Disconnected — messaging unavailable");
            setChatState("initial");
            hasConnected.current = false;
        };

        return () => {
            if (
                socket.current &&
                socket.current.readyState === WebSocket.OPEN
            ) {
                socket.current.close();
                setIsConnected(false);
                hasConnected.current = false;
            }
        };
    }, [wsUrl]);

    const handleResumePanelToggle = async () => {
        setShowResumePanel((prev) => !prev);
        if (!showResumePanel) {
            await fetchResumeInfo();
        }
    };

    const handleResumeChoice = (mode) => {
        setResumeMode(mode);
        setResumeMessage(
            mode === "existing"
                ? "The saved resume will be used by the agent when a job form asks for it."
                : "Upload a new resume to replace the saved one."
        );
        setResumeError("");
    };

    const handleResumeFileChange = (event) => {
        setResumeFile(event.target.files?.[0] || null);
        setResumeError("");
        setResumeMessage("");
    };

    const handleResumeUpload = async () => {
        const userId = localStorage.getItem("userId");
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!userId || !apiUrl) return;
        if (!resumeFile) {
            setResumeError("Choose a resume file first.");
            return;
        }

        setResumeLoading(true);
        setResumeError("");
        setResumeMessage("");

        try {
            const formData = new FormData();
            formData.append("resume", resumeFile);

            await axios.post(`${apiUrl}/profile/${userId}/resume`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setResumeInfo({
                hasResume: true,
                resumeFileName: resumeFile.name,
            });
            setResumeMode("existing");
            setResumeMessage("Resume uploaded successfully.");
            setResumeFile(null);
            if (resumeInputRef.current) {
                resumeInputRef.current.value = "";
            }
        } catch (error) {
            setResumeError(
                error.response?.data?.error || "Failed to upload the resume."
            );
        } finally {
            setResumeLoading(false);
        }
    };

    const handleRefreshResume = async () => {
        setResumeLoading(true);
        try {
            await fetchResumeInfo();
            setResumeMessage("Resume status refreshed.");
        } finally {
            setResumeLoading(false);
        }
    };

    const handleCookieExtraction = ({
        domainName,
        domains,
        cookieName,
        successMessage,
        buttonText,
        buttonLink,
        socketMessageType,
    }) => {
        const extensionId = "aeaachdmgnjpbihcafbkkpjmkjbkonjh";

        const checkCookies = () => {
            if (window.chrome?.runtime) {
                window.chrome.runtime.sendMessage(
                    extensionId,
                    { action: "fetchCookies", domains },
                    (response) => {
                        if (window.chrome.runtime.lastError) {
                            console.error(
                                `Error fetching cookies for ${domainName}:`,
                                window.chrome.runtime.lastError.message
                            );
                        } else if (response && response.cookies) {
                            console.log(
                                `${domainName} cookies:`,
                                response.cookies
                            );
                            const requiredCookie = response.cookies.find(
                                (cookie) => cookie.name === cookieName
                            );

                            if (requiredCookie) {
                                console.log(
                                    `Required cookies found for ${domainName}`
                                );

                                if (
                                    socket.current &&
                                    socket.current.readyState === WebSocket.OPEN
                                ) {
                                    socket.current.send(
                                        JSON.stringify({
                                            type: socketMessageType,
                                            text: response.cookies,
                                        })
                                    );
                                }

                                setMessages((prev) => [
                                    ...prev.filter((msg) => !msg.isButton),
                                    {
                                        id: Date.now(),
                                        text: successMessage,
                                        sender: "other",
                                    },
                                ]);
                                setIsButtonDisplayed(false);
                                if (cookieCheckIntervalRef.current) {
                                    clearInterval(
                                        cookieCheckIntervalRef.current
                                    );
                                    cookieCheckIntervalRef.current = null;
                                }
                            } else if (!isButtonDisplayed) {
                                console.log(
                                    `Required cookies missing for ${domainName}. Sending button to user.`
                                );
                                setMessages((prev) => [
                                    ...prev.filter((msg) => !msg.isButton),
                                    {
                                        id: Date.now(),
                                        text: `To complete the task, I need access to your ${domainName} account. Please click the button below to open ${domainName} and log in to your account.`,
                                        sender: "other",
                                        isButton: true,
                                        buttonText,
                                        buttonLink,
                                    },
                                ]);
                                setIsButtonDisplayed(true);
                            }
                        }
                    }
                );
            } else {
                console.error("Chrome runtime API not available.");
            }
        };

        // Perform initial check
        checkCookies();

        // Start polling every 5 seconds if not already polling
        if (!cookieCheckIntervalRef.current) {
            cookieCheckIntervalRef.current = setInterval(checkCookies, 5000);
        }

        // Update the socket onclose handler to clear the interval
        const originalOnClose = socket.current?.onclose;
        if (socket.current) {
            socket.current.onclose = (event) => {
                if (cookieCheckIntervalRef.current) {
                    clearInterval(cookieCheckIntervalRef.current);
                    cookieCheckIntervalRef.current = null;
                }
                if (originalOnClose) {
                    originalOnClose(event);
                }
            };
        }
    };

    // Clean up interval on component unmount
    useEffect(() => {
        return () => {
            if (cookieCheckIntervalRef.current) {
                clearInterval(cookieCheckIntervalRef.current);
                cookieCheckIntervalRef.current = null;
            }
        };
    }, []);

    const handleSendMessage = () => {
        if (
            newMessage.trim() &&
            socket.current?.readyState === WebSocket.OPEN
        ) {
            setMessages((prev) => [
                ...prev,
                { id: Date.now(), text: newMessage, sender: "user" },
            ]);

            socket.current.send(
                JSON.stringify({
                    text: newMessage,
                    type: chatState === "initial" ? "initial" : "continuation",
                })
            );

            setNewMessage("");
        }
    };

    const toggleImageMessages = () => {
        setShowImages((prevState) => !prevState);
    };

    const chat = useMemo(() => messages.map((message) => ({
        text: message.isImage ? "Browser screenshot received from the agent." : message.text,
        timestamp: new Date(message.id).toISOString(),
        sender: message.sender === "user" ? "user" : "bot",
        image: message.isImage ? { src: message.text, alt: "Browser screenshot from the agent", hidden: !showImages } : undefined,
        action: message.isButton ? { href: message.buttonLink, label: message.buttonText } : undefined,
    })), [messages, showImages]);

    return (
        <div className="containerr">
            {!isMobile && <Header />}
            <div className="page-container">
                <SideBar activePage="BrowserAgent" />
                <main id="main-content" className="doc-container agent-chat-page browser-agent-chat" tabIndex={-1}>
                    <div className="agent-chat-heading">
                        <div>
                            <p className="agent-eyebrow">Accessible browser assistant</p>
                            <h1 id="browser-agent-heading" ref={mainRef} tabIndex={-1} className="display-title">Browser Agent</h1>
                            <p className="agent-description">Get help with job applications, rides, travel tickets, and food orders.</p>
                        </div>
                        <p className="agent-status" role="status" aria-live="polite">
                            <span className={chatState === "processing" ? "status-dot busy" : "status-dot"} aria-hidden="true" />
                            {isConnected ? (chatState === "processing" ? "Responding" : "Online") : connectionStatus}
                        </p>
                    </div>
                    <section className="chat-box" aria-label="Browser Agent conversation">
                        <div className="browser-agent-actions" role="group" aria-label="Conversation controls">
                            <button type="button" onClick={toggleImageMessages} aria-pressed={showImages}>
                                <BsImages aria-hidden="true" />
                                <span>{showImages ? "Hide Images" : "Show Images"}</span>
                            </button>
                            <button type="button" onClick={handleResumePanelToggle} aria-expanded={showResumePanel} aria-controls="resume-manager-panel">
                                <FaFileUpload aria-hidden="true" />
                                <span>Resume Manager</span>
                            </button>
                        </div>
                    {showResumePanel && (
                        <div id="resume-manager-panel" role="region" aria-label="Resume Manager" className="border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-4 py-2.5 shadow-inner">
                            <div className="mx-auto max-w-4xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
                                {/* Left Side: Status / Info */}
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <div className="flex items-center gap-1.5 text-black dark:text-white font-medium">
                                        <FaFileUpload className="w-4 h-4 text-gray-700 dark:text-gray-300" aria-hidden="true" />
                                        <span>Resume Manager:</span>
                                    </div>
                                    {resumeInfo.hasResume ? (
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800">
                                                <FaCheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400" aria-hidden="true" />
                                                <span className="truncate max-w-[180px] sm:max-w-xs">{resumeInfo.resumeFileName}</span>
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800">
                                            No resume saved yet
                                        </span>
                                    )}
                                    {resumeMessage && (
                                        <span role="status" aria-live="polite" className="text-xs text-emerald-800 dark:text-emerald-300 font-medium">
                                            {resumeMessage}
                                        </span>
                                    )}
                                    {resumeError && (
                                        <span role="alert" aria-live="assertive" className="text-xs text-red-800 dark:text-red-400 font-medium">
                                            {resumeError}
                                        </span>
                                    )}
                                </div>

                                {/* Right Side: Actions or Inline Uploader */}
                                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                                    {resumeInfo.hasResume && resumeMode !== "replace" ? (
                                        <>
                                            <button
                                                onClick={() => handleResumeChoice("replace")}
                                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 text-black dark:text-white transition duration-200 dark:border-gray-500"
                                            >
                                                Replace
                                            </button>
                                            <button
                                                onClick={handleRefreshResume}
                                                disabled={resumeLoading}
                                                title="Refresh resume status"
                                                aria-label="Refresh resume status"
                                                className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200 disabled:opacity-50 transition duration-200"
                                            >
                                                <FaRedoAlt className={`w-3.5 h-3.5 ${resumeLoading ? 'animate-spin' : ''}`} />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <input
                                                ref={resumeInputRef}
                                                type="file"
                                                accept=".pdf,.doc,.docx,.txt"
                                                onChange={handleResumeFileChange}
                                                aria-label="Select resume file"
                                                className="block text-xs text-gray-800 dark:text-gray-200 file:mr-2 file:rounded-md file:border-0 file:bg-black file:px-2.5 file:py-1.5 file:text-xs file:font-semibold file:text-white dark:file:bg-white dark:file:text-black max-w-[200px]"
                                            />
                                            <button
                                                onClick={handleResumeUpload}
                                                disabled={resumeLoading || !resumeFile}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 transition duration-200"
                                            >
                                                <FaFileUpload className="w-3.5 h-3.5" aria-hidden="true" />
                                                <span>Upload</span>
                                            </button>
                                            {resumeInfo.hasResume && (
                                                <button
                                                    onClick={() => {
                                                        setResumeMode(null);
                                                        setResumeMessage("");
                                                        setResumeError("");
                                                    }}
                                                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 text-black dark:text-white transition duration-200 dark:border-gray-500"
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                        <div className="chat-window-container" role="log" aria-label="Browser Agent messages" aria-live="off" aria-relevant="additions text" aria-busy={chatState === "processing"} tabIndex={0}>
                            <Chat chat={chat} assistantName="Browser Agent" announcementRef={liveRegionRef} />
                            {chatState === "processing" && (
                                <div className="assistant-thinking" role="status">
                                    <BouncingDotsLoader />
                                    <span>Browser Agent is working…</span>
                                </div>
                            )}
                        </div>
                        <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="visually-hidden" />
                        <ChatComposer inputRef={inputRef} value={newMessage} onChange={setNewMessage} onSubmit={handleSendMessage} disabled={!isConnected} placeholder="Message the Browser Agent" assistantName="Browser Agent" />
                    </section>
                </main>
            </div>
        </div>
    );
}

export default BrowserAgent;
